// The prose the essay workshop engine says of its own accord, keyed so an
// edition can override any line with the topic in it. Placeholders are filled
// by the engine: {a} / {b} the case studies' names, {unit} / {units} the
// case-study noun, {theme} / {themes} the theme noun, {n} a count written out,
// {anchor} the anchor theme in lower case, {j} / {ja} / {jb} judgement text,
// {region} the region two refused case studies share, {paragraphs} the
// number of body paragraphs the learner chose, written out. The pair
// templates at the end take {A} / {B} (capitalised where they open the
// sentence), each case study's compare lines as {changedA}, {limitedB} and
// so on, {contextA} / {contextB}, and {title} for a reading's title.
//
// Every default here was read off the shipped workshops where it was identical
// or differed only by the topic word; the per-edition overrides in the
// fixtures carry the rest.

import type { WorkshopCopyKey } from '../types';

export const WORKSHOP_COPY_DEFAULTS: Record<WorkshopCopyKey, string> = {
  chooseLede: 'First, choose the {unit} you have studied.',
  regionRefused: 'Both {units} are in {region}, so this pair breaks the region rule for this question.',
  matchLede: 'Match each of the {n} {themes} to the sentence that shows it.',
  matchLede1: 'Start with {a}: match each {theme} to the fact that shows it.',
  matchLede2: 'Now {b}. The {n} {themes} are the same. Match each one to the fact that shows it.',
  matchHowto: 'Select a {theme}, then select the fact that shows it. Select a matched pair to undo it.',
  matchTableHowto: 'Each row is one {theme}, the evidence for it, and the question you will have to judge. You can open this table again on every later step.',
  argueLede: 'There is more than one reading of the claim to weigh. Read all three before you decide.',
  balanceHead: 'This question needs balance',
  balance: 'You will decide your own argument. You will also choose the counter-argument you are going to answer, because a strong essay deals with the evidence against its view as well as the evidence for it.',
  compareHowto: 'For each {theme}, judge what each {unit} did. There is no right answer. This is your judgement, and your evidence has to support it.',
  compareSummary: 'You judged that the two {units} had the same effect on {same} of {themeCount} {themes}. Overall, you judged that {overallBoth}.',
  comparePositionNote: 'When you compared the {units}, you judged that {overallBoth}.',
  bothSame: 'both {a} and {b} {j}',
  bothDifferent: '{a} {ja} and {b} {jb}',
  rankHowto: 'Rank the {n} {themes} from the one that mattered most to the one that mattered least. Use the arrows. There is no right order. This is your judgement, and you will have to defend it.',
  rankTally: 'Your highest-ranked {themes} become your paragraphs, in this order. You decide your argument on the next screen.',
  rankTest: 'Take your top {theme} away. Would the outcome still have been the same? If the answer is yes, it may not belong at the top.',
  positionLabel: '1. What is your position?',
  positionNote: 'Choose the position you are able to defend.',
  positionYes: 'You take a position that answers the question.',
  positionNo: 'Your introduction does not take a position yet. Say what your answer to the question is.',
  answerNo: 'Your first sentence should answer the question.',
  linkNo: 'End by linking back. Say what this paragraph shows about the question.',
  analysisNo: 'Your facts are stated but not explained. Add a sentence that starts with This matters because.',
  outline: 'This essay considers',
  paragraphsLede: 'Your essay has {paragraphs} body paragraphs. Each one takes one {theme}. Your introduction comes first. Then you take one paragraph at a time: plan it, then write it, before you go on to the next.',
  leftOut: 'You have about 45 minutes for this essay in the exam, which is time for three or four paragraphs. To bring it in, change your ranking or the number of paragraphs.',
  conclusionAnswerStarter: 'Overall, ',
  connectionStarterAnchor: 'This made the other {themes} more dangerous because ',
  connectionStarterOther: 'This made {anchor} more dangerous because ',
  recallExample: '',
  recallExampleAnchor: '',
  coachExample: 'For example: why did this matter?',
  pairContext: '{contextA} {contextB}',
  tsSimilar: '{A} {changedA}, and {B} {changedB}.',
  tsLimited: '{A} {limitedA}, and {B} {limitedB}.',
  tsContrast: '{A} {changedA}, while {B} {limitedB}.',
  tsNeed: 'You will need to show {showA}, then {showB}.',
  posBuild: '{title}, in both {A} and {B}',
  pairReason: '{reasonA}, and {reasonB}',
  pairCounter: '{counterA}, and {counterB}',
  pairMixedReason: '{reasonA}, while {counterB}',
  pairMixedCounter: '{counterA}, while {reasonB}',
};

export const WORKSHOP_COPY_KEYS = Object.keys(WORKSHOP_COPY_DEFAULTS) as WorkshopCopyKey[];

/**
 * The steps, in order (Leah's instructions, "Essay workshops" 1 to 11). The
 * choice of case studies is on the start screen, not a step. `paragraph`
 * repeats once per body paragraph the learner chose.
 */
export const WORKSHOP_STEP_IDS = ['question', 'revise', 'match', 'argue', 'missed', 'paragraphs', 'intro', 'paragraph', 'conclusion', 'essay'] as const;

/**
 * The groups the coach's quick answers are keyed by: the steps that have
 * their own, plus `choose` (the start screen's choice) and `write` (every
 * written part). A paragraph step shows `paragraphs` and `write` together.
 */
export const WORKSHOP_QUICK_KEYS = ['question', 'choose', 'revise', 'match', 'argue', 'missed', 'paragraphs', 'write', 'essay'] as const;

/** Engine lines the rules fix word for word (Leah's instructions and the brief). Not copy keys: a workshop cannot change them. */
export const WORKSHOP_FIXED_LINES = {
  readCarefully: 'Read this screen carefully. On the next screen you will write down the most important facts from memory.',
  recallWhy: 'Writing facts from memory makes them stick far better than reading them again.',
  flashcardsWhy1: 'In the exam you have no notes. Everything you write has to come out of your memory, in about 45 minutes. Reading facts again feels like learning, but most of it fades within days. What makes a fact stick is pulling it out of your memory yourself, especially when it is a struggle.',
  flashcardsWhy2: 'These cards are the facts you missed earlier, the ones you are most likely to forget. A card you get wrong is not a failure. Struggling to remember it, then seeing the answer, is exactly how it sticks.',
  perspectiveOptional: 'The markbands describe evaluation of different perspectives, so bring one in where it helps your argument. It does not have to agree with you. You do not need one in every paragraph.',
  paragraphsNote: 'The markbands do not set a number of paragraphs. Choose what you can write well in about 45 minutes.',
} as const;
